@let invoice = model.invoice$ | async;
@let pageLoading = pageLoading$ | async;

@if (pageLoading) {
  <div class="content-skeleton">
    <p-skeleton animation="wave" height="100%" width="100%"></p-skeleton>
  </div>
}

<app-fleet-page-header
  (export)="model.exportReport($event)"
  [class.hidden-component]="pageLoading"
  [defaultReturnRoute]="'billing/history'"
  [showExport]="true"
  [showMobileMenuButton]="false"
  [showPdfExportOption]="false"
  [showReturnButton]="true"
  [showSearch]="false"
  [templates]="{ returnButton, rightControls, titleBadge }"
  [titleText]="(title | async) ?? ''"
></app-fleet-page-header>

<ng-template #titleBadge>
  <div class="flex--row align-items-center">
    <app-counter [brackets]="false" [text]="''" [value]="invoice?.items?.length ?? 0">
      <app-icon-device [height]="20" [width]="20"></app-icon-device>
    </app-counter>

    <app-icon-info
      [appTooltip]="'tsp-admin.billing.device-count-info' | translate"
      [height]="16"
      [tooltipMobile]="true"
      [tooltipPosition]="'top'"
      [width]="16"
      class="ml-6"
    ></app-icon-info>
  </div>
</ng-template>

<p [class.hidden-component]="pageLoading" class="billing-tagline text-14 color-gray-500 p-l-32">
  {{ 'tsp-admin.billing.tagline' | translate }}
</p>

<div [class.hidden-component]="pageLoading" class="mb-8 text-18 p-l-32">
  <h3 class="text-18 fw-600">
    SMS / WhatsApp:
    <a [routerLink]="['/sms/logs']">
      {{ invoice?.messaging?.messagesNumber || 0 }} {{ 'common.messages' | translate }}
    </a>
    x ${{
      (invoice?.messaging?.amount ?? 0) / (invoice?.messaging?.messagesNumber || 0 || 1)
        | number: '.2-2'
    }}
    = ${{ invoice?.messaging?.amount ?? 0 | number: '.2-2' }}
  </h3>
</div>

<div
  [class.hidden-component]="pageLoading"
  [ngClass]="{ 'flex-grow-1': invoice?.items?.length === 0 }"
  class="table-wrapper"
>
  <app-data-grid
    [columns]="(state.columns$ | async) ?? []"
    [data]="(state.items$ | async) ?? []"
    [isHeaderWrap]="(languageService.current$ | async) === LanguageCode.Spanish"
    [isLoading]="!!(state.isPending$ | async)"
    [noDataDescription]="'tsp-admin.billing.grid.no-data-description' | translate"
    [noDataIconTemplate]="gridEmptyStateIconTmpl"
    [noDataTitle]="'tsp-admin.billing.grid.no-data-title' | translate"
    [paginationEnabled]="false"
    [sortOrder]="undefined"
    [templates]="{
      fleetAccounts,
      tntDays,
      standardDays,
      advancedDays,
      premiumDays,
      currentPricingPlan,
      assignmentDate,
      renewalDate,
      annualRate,
    }"
  ></app-data-grid>
</div>

<ng-template #rightControls>
  <app-icon-billing [color]="'var(--color-primary-500)'"></app-icon-billing>
  <h3 class="text-18 fw-600">
    {{ 'tsp-admin.billing.grid.amount' | translate }}:
    {{ 'tsp-admin.billing.grid.minimum-fee' | translate }}: ${{
      invoice?.minimumMonthlyFee | number: '.2-2'
    }}
    | {{ 'tsp-admin.billing.grid.calculated-amount' | translate }}: ${{
      invoice?.calculatedAmount | number: '.2-2'
    }}
    | {{ 'tsp-admin.billing.grid.billed-amount' | translate }}: ${{
      invoice?.grandTotal | number: '.2-2'
    }}
  </h3>
</ng-template>

<ng-template #fleetAccounts let-row="row">
  <span class="text-14 color-gray-900">
    @if (row.fleetAccounts?.length) {
      @for (fleetAccount of row.fleetAccounts; track fleetAccount.id) {
        {{ fleetAccount.name }}{{ $last ? '' : ', ' }}
      }
    } @else {
      ---
    }
  </span>
</ng-template>

<ng-template #tntDays let-row="row">
  <span class="text-14 color-gray-900">
    @if (row.plans[PricingPlans.TnT]?.daysCharged) {
      {{ row.plans[PricingPlans.TnT].daysCharged }}
    } @else {
      ---
    }
  </span>
</ng-template>

<ng-template #standardDays let-row="row">
  <span class="text-14 color-gray-900">
    @if (row.plans[PricingPlans.Standard]?.daysCharged) {
      {{ row.plans[PricingPlans.Standard].daysCharged }}
    } @else {
      ---
    }
  </span>
</ng-template>

<ng-template #advancedDays let-row="row">
  <span class="text-14 color-gray-900">
    @if (row.plans[PricingPlans.Advanced]?.daysCharged) {
      {{ row.plans[PricingPlans.Advanced].daysCharged }}
    } @else {
      ---
    }
  </span>
</ng-template>

<ng-template #premiumDays let-row="row">
  <span class="text-14 color-gray-900">
    @if (row.plans[PricingPlans.Premium]?.daysCharged) {
      {{ row.plans[PricingPlans.Premium].daysCharged }}
    } @else {
      ---
    }
  </span>
</ng-template>

<ng-template #currentPricingPlan let-row="row">
  <span class="text-14 color-gray-900">
    {{ 'tsp-admin.fleets.pricing-plans.' + row.currentPricingPlan | translate }}
  </span>
</ng-template>

<ng-template #assignmentDate let-row="row">
  <span class="text-14 color-gray-900">
    @if (row.currentPlanAssignment?.assignmentDate) {
      {{ row.currentPlanAssignment?.assignmentDate | formatDate: 'grid-date-time' }}
    } @else {
      ---
    }
  </span>
</ng-template>

<ng-template #renewalDate let-row="row">
  <span class="text-14 color-gray-900">
    @if (row.currentPlanAssignment?.renewalDate) {
      {{ row.currentPlanAssignment?.renewalDate | formatDate: 'grid-date-time' }}
    } @else {
      ---
    }
  </span>
</ng-template>

<ng-template #annualRate let-row="row">
  <span class="text-14 color-gray-900">
    @if (row.currentPlanAssignment?.rate) {
      ${{ row.currentPlanAssignment?.rate | number: '.2-2' }}
    } @else {
      ---
    }
  </span>
</ng-template>

<ng-template #gridEmptyStateIconTmpl>
  <i>
    <app-icon-pay-disable [color]="'var(--color-gray-900)'"></app-icon-pay-disable>
  </i>
</ng-template>

<ng-template #returnButton let-isMobile="isMobile">
  <p-button
    (click)="goBack()"
    [class.mr-16]="!isMobile"
    [severity]="'secondary'"
    class="button-back h--40 flex"
    icon="pi"
  >
    <app-icon-arrow [height]="24" [width]="24" direction="left"></app-icon-arrow>
  </p-button>
</ng-template>
